import { useState } from 'react';
import {
    DEFAULT_ELASTIC_SIZE,
    DEFAULT_ELASTIC_WEAR,
    MAX_ELASTIC_TEETH,
    randomElasticColor
} from './model';
import type { ElasticPlacement } from './model';
import { createPlacementId } from './persistence';
import { ElasticsChart } from './ElasticsChart';
import { ElasticsDrawingBanner } from './ElasticsDrawingBanner';
import { PlacedElasticsList } from './PlacedElasticsList';

interface OdontogramProps {
    placements: ElasticPlacement[];
    onChange: (next: ElasticPlacement[]) => void;
    /** Disables all interaction (used inside read-only recaps). */
    readOnly?: boolean;
    /** Chart capped near 520px with the list beside it on a wide pane, stacked and centred on a narrow one. */
    compact?: boolean;
}

// Teeth are tapped in placement order (FDI, frontal view); tapping the first again closes the band.
// Owns only the drawing state: every change emits the full placement list to the parent.
export function Odontogram({
    placements,
    onChange,
    readOnly = false,
    compact = false
}: OdontogramProps) {
    // `drawing` = a placement is in progress; `selected` = its teeth so far.
    const [drawing, setDrawing] = useState(false);
    const [selected, setSelected] = useState<number[]>([]);

    const commit = (teeth: number[]) => {
        const placement: ElasticPlacement = {
            id: createPlacementId(),
            name: `Elastic ${placements.length + 1}`,
            teeth,
            size: DEFAULT_ELASTIC_SIZE,
            wear: DEFAULT_ELASTIC_WEAR,
            color: randomElasticColor()
        };
        onChange([...placements, placement]);
        setDrawing(false);
        setSelected([]);
    };

    const handleToothClick = (fdi: number) => {
        if (readOnly || !drawing) return;
        // Tapping the first tooth again closes the band (needs ≥2 teeth).
        if (selected.length >= 2 && fdi === selected[0]) {
            commit(selected);
            return;
        }
        // A tooth can only carry one hook per band; ignore repeats of a non-first.
        if (selected.includes(fdi)) return;
        if (selected.length >= MAX_ELASTIC_TEETH) return;
        setSelected([...selected, fdi]);
    };

    const handleStartDrawing = () => {
        setDrawing(true);
        setSelected([]);
    };

    const handleCancelDrawing = () => {
        setDrawing(false);
        setSelected([]);
    };

    const handleUndoLast = () => {
        setSelected((prev) => prev.slice(0, -1));
    };

    const handleRemove = (id: string) => {
        onChange(placements.filter((p) => p.id !== id));
    };

    const handleSizeChange = (id: string, size: string) => {
        onChange(placements.map((p) => (p.id === id ? { ...p, size } : p)));
    };

    const handleWearChange = (id: string, wear: string) => {
        onChange(placements.map((p) => (p.id === id ? { ...p, wear } : p)));
    };

    // Toggle a tooth's bond button between buccal and lingual. Applies to every
    // band that lands on that tooth so the single drawn button stays consistent.
    const handleToggleButton = (fdi: number) => {
        const makeLingual = !placements.some((p) => p.lingualTeeth?.includes(fdi));
        onChange(
            placements.map((p) => {
                if (!p.teeth.includes(fdi)) return p;
                const set = new Set(p.lingualTeeth ?? []);
                if (makeLingual) set.add(fdi);
                else set.delete(fdi);
                return { ...p, lingualTeeth: [...set] };
            })
        );
    };

    const handleNameChange = (id: string, name: string) => {
        onChange(placements.map((p) => (p.id === id ? { ...p, name } : p)));
    };

    const handleClearAll = () => {
        handleCancelDrawing();
        onChange([]);
    };

    const handleReorder = (id: string, direction: -1 | 1) => {
        const idx = placements.findIndex((p) => p.id === id);
        const target = idx + direction;
        if (idx === -1 || target < 0 || target >= placements.length) return;
        const next = [...placements];
        const [moved] = next.splice(idx, 1);
        next.splice(target, 0, moved);
        onChange(next);
    };

    const chart = (
        <ElasticsChart
            placements={placements}
            selected={selected}
            drawing={drawing}
            readOnly={readOnly}
            onToothClick={handleToothClick}
            onRemoveBand={handleRemove}
            onButtonClick={handleToggleButton}
            compact={compact}
        />
    );
    const banner = !readOnly && drawing && (
        <ElasticsDrawingBanner
            selected={selected}
            onUndoLast={handleUndoLast}
            onCancel={handleCancelDrawing}
        />
    );
    const list = (
        <PlacedElasticsList
            placements={placements}
            readOnly={readOnly}
            drawing={drawing}
            onStartDrawing={handleStartDrawing}
            onClearAll={handleClearAll}
            onRemove={handleRemove}
            onSizeChange={handleSizeChange}
            onWearChange={handleWearChange}
            onNameChange={handleNameChange}
            onReorder={handleReorder}
            compact={compact}
        />
    );

    // Wrapping flex: the list (340px basis) drops under the 520px chart once the pane is below ~900px.
    if (compact) {
        return (
            <div className="flex flex-wrap items-start justify-center gap-x-8 gap-y-4">
                <div className="min-w-0 flex-[0_1_520px]">{chart}</div>
                <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-4">
                    {banner}
                    {list}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {chart}
            {banner}
            {list}
        </div>
    );
}
